/**
 * Completion content for the "schedule-services" activity.
 * Self-managed: fetches service requests for the care plan, presents an
 * AppointmentForm per service request, and completes the activity once
 * all appointments are created. Each created Appointment is linked back to
 * its ServiceRequest (via basedOn) and the CarePlan (via supportingInformation).
 * @agentic connections
 */

import {
  Alert,
  Badge,
  Button,
  Collapse,
  Group,
  Paper,
  Skeleton,
  Stack,
  Text,
} from "@mantine/core";
import { useForm } from "@mantine/form";
import {
  IconAlertCircle,
  IconCalendarPlus,
  IconCheck,
  IconChevronDown,
  IconChevronUp,
} from "@tabler/icons-react";
import {
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useMemo,
  useState,
} from "react";
import { useMedplum, useMedplumProfile } from "@medplum/react";
import type {
  CarePlan,
  Practitioner,
  PractitionerRole,
  Reference,
  ServiceRequest,
} from "@medplum/fhirtypes";
import { FormStep, SteppedForm } from "@benchmark/ui/components/atomic";
import { NextTaskOwnerSection } from "@/components/shared/forms/NextTaskOwnerSection";
import { CareTimeActivityDurationField } from "@/components/domain/care-time/CareTimeActivityDurationField";
import { useTeeCareTimeForTask } from "@/hooks/domain/useTeeCareTimeForEncounter";
import { AppointmentForm } from "@/components/domain/appointment/Appointment.form";
import { useFormProtection } from "@benchmark/ui/hooks";
import { useCarePlanServiceRequests } from "@/hooks/domain/useCarePlanServiceRequests";
import { handleActivityCompletion } from "@benchmark/core/workflows";
import { findBenchmarkPractitionerRole } from "@benchmark/core/services";
import { toDateInputValue } from "@benchmark/ui/utils";
import { getResourceIdFromReference } from "@benchmark/core/utils";
import { showSuccessNotification } from "@benchmark/ui/utils";
import {
  getCarePlanIdFromTask,
  getServiceTypeDisplay,
  getConditionDisplays,
  getSelectedServiceDisplay,
  hasSelectedService,
} from "@benchmark/core/resolvers";
import type { CompletionContentProps } from "./DefaultCompletionContent";
import { CONFIG } from "@/app/config";

/**
 * Tracks created appointments keyed by ServiceRequest ID.
 * @agentic connections
 */
type AppointmentTracker = Record<string, string>;

/**
 * Ref handle for ScheduleServicesCompletionContent to participate in close protection.
 * @agentic connections
 */
export type ScheduleServicesCompletionContentRef = {
  checkProtection: () => Promise<boolean>;
};

/**
 * Card displaying a ServiceRequest with an expandable appointment form.
 * @agentic connections
 */
const ServiceAppointmentCard = ({
  sr,
  index,
  totalCount,
  isExpanded,
  appointmentId,
  patientId,
  carePlanId,
  carePlanDisplay,
  onToggle,
  onAppointmentCreated,
}: {
  sr: ServiceRequest;
  index: number;
  totalCount: number;
  isExpanded: boolean;
  appointmentId: string | undefined;
  patientId: string;
  carePlanId: string;
  carePlanDisplay: string;
  onToggle: () => void;
  onAppointmentCreated: (appointmentId: string) => void;
}) => {
  const serviceTypeDisplay = getServiceTypeDisplay(sr);
  const conditions = getConditionDisplays(sr);
  const selectedService = hasSelectedService(sr)
    ? getSelectedServiceDisplay(sr)
    : undefined;
  const isScheduled = !!appointmentId;

  const badgeLabel =
    totalCount > 1
      ? `${index + 1} \u00B7 ${serviceTypeDisplay.toUpperCase()}`
      : serviceTypeDisplay.toUpperCase();

  const basedOn: Reference<ServiceRequest>[] = sr.id
    ? [
        {
          reference: `ServiceRequest/${sr.id}`,
          display: serviceTypeDisplay,
        },
      ]
    : [];

  const supportingInformation = [
    {
      reference: `CarePlan/${carePlanId}`,
      display: carePlanDisplay,
    },
  ];

  return (
    <Paper
      p="md"
      withBorder
      style={
        isScheduled
          ? {
              borderColor: "var(--mantine-color-green-4)",
              borderWidth: 2,
            }
          : isExpanded
            ? {
                borderColor: "var(--mantine-color-blue-4)",
                borderWidth: 2,
              }
            : undefined
      }
    >
      <Stack gap="sm">
        <Group justify="space-between" align="center">
          <Group gap="sm">
            <Badge variant="filled" color="purple" size="lg" radius="sm">
              {badgeLabel}
            </Badge>
            {isScheduled && (
              <Badge
                variant="light"
                color="green"
                size="sm"
                leftSection={<IconCheck size={12} />}
              >
                Appointment Scheduled
              </Badge>
            )}
          </Group>

          {!isScheduled && (
            <Button
              variant="subtle"
              size="compact-sm"
              onClick={onToggle}
              rightSection={
                isExpanded ? (
                  <IconChevronUp size={14} />
                ) : (
                  <IconChevronDown size={14} />
                )
              }
              leftSection={!isExpanded ? <IconCalendarPlus size={14} /> : null}
            >
              {isExpanded ? "Collapse" : "Schedule Appointment"}
            </Button>
          )}
        </Group>

        <Group gap={4} wrap="wrap">
          {conditions.map((c) => (
            <Badge key={c} size="xs" variant="light" color="purple">
              {c}
            </Badge>
          ))}
        </Group>

        {selectedService && (
          <Group gap="xs" wrap="nowrap">
            <Text size="sm" fw={500} style={{ flexShrink: 0 }}>
              Selected Service:
            </Text>
            <Text size="sm">{selectedService}</Text>
          </Group>
        )}

        <Collapse in={isExpanded && !isScheduled}>
          <Paper p="sm" bg="gray.0" radius="sm">
            <AppointmentForm
              patientId={patientId}
              onSuccess={onAppointmentCreated}
              onCancel={onToggle}
              basedOn={basedOn}
              supportingInformation={supportingInformation}
            />
          </Paper>
        </Collapse>
      </Stack>
    </Paper>
  );
};

/**
 * Self-managed completion form for schedule-services: creates FHIR Appointments
 * linked to each ServiceRequest and the CarePlan.
 * @agentic connections
 */
export const ScheduleServicesCompletionContent = forwardRef<
  ScheduleServicesCompletionContentRef,
  CompletionContentProps
>(function ScheduleServicesCompletionContent(
  {
    task,
    onDone,
    onCancel,
    onDirtyChange,
    nextActivityLabel,
    nextActivityDefaultOwner,
    nextActivityAppliedRule,
    nextTaskOwnerOverride,
    onNextTaskOwnerOverrideChange,
    isResolvingNextTaskOwner = false,
  }: CompletionContentProps,
  ref,
) {
  const form = useForm({ initialValues: { placeholder: "" } });
  const medplum = useMedplum();
  const profile = useMedplumProfile() as Practitioner | undefined;

  const { checkProtection, ConfirmationModal } = useFormProtection(form, {
    enabled: true,
    blockNavigation: false,
    confirmationMessage:
      "You have unsaved changes. Are you sure you want to close?",
  });

  useImperativeHandle(
    ref,
    () => ({
      checkProtection,
    }),
    [checkProtection],
  );

  const [practitionerRole, setPractitionerRole] = useState<
    PractitionerRole | undefined
  >(undefined);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [expandedSrId, setExpandedSrId] = useState<string | null>(null);
  const [createdAppointments, setCreatedAppointments] =
    useState<AppointmentTracker>({});
  const [carePlanDisplay, setCarePlanDisplay] = useState("Care Plan");
  const [careMinutes, setCareMinutes] = useState<number | undefined>(undefined);
  const teeForTask = useTeeCareTimeForTask();

  const carePlanId = getCarePlanIdFromTask(task);
  const patientId = getResourceIdFromReference(task.for?.reference, "Patient");

  const {
    serviceRequests,
    isLoading: srLoading,
    error: srError,
  } = useCarePlanServiceRequests(carePlanId);

  useEffect(() => {
    if (!profile?.id) return;
    findBenchmarkPractitionerRole(medplum, {
      reference: `Practitioner/${profile.id}`,
      benchmarkOrganizationId: CONFIG.VITE_BENCHMARK_ORGANIZATION_ID,
    })
      .then(setPractitionerRole)
      .catch(() => setPractitionerRole(undefined));
  }, [medplum, profile?.id]);

  useEffect(() => {
    if (!carePlanId) return;
    medplum
      .readResource("CarePlan", carePlanId)
      .then((cp: CarePlan) => {
        setCarePlanDisplay(cp.title || cp.description || "Care Plan");
      })
      .catch(() => {});
  }, [medplum, carePlanId]);

  const schedulableRequests = useMemo(
    () => serviceRequests.filter((sr) => sr.status === "active" && sr.id),
    [serviceRequests],
  );

  const allScheduled = useMemo(
    () =>
      schedulableRequests.length > 0 &&
      schedulableRequests.every((sr) => sr.id && createdAppointments[sr.id]),
    [schedulableRequests, createdAppointments],
  );

  const scheduledCount = useMemo(
    () =>
      schedulableRequests.filter((sr) => sr.id && createdAppointments[sr.id])
        .length,
    [schedulableRequests, createdAppointments],
  );

  const steps = useMemo(
    () => [
      {
        id: "appointments",
        title: "Appointments",
        subtitle: `Schedule appointments for ${schedulableRequests.length} service${schedulableRequests.length !== 1 ? "s" : ""}`,
        validate: () =>
          scheduledCount === 0
            ? { appointments: "At least one appointment must be scheduled" }
            : null,
      },
      {
        id: "assignment",
        title: "Assignment",
        subtitle: "Review next task assignment",
      },
    ],
    [schedulableRequests.length, scheduledCount],
  );

  const handleAppointmentCreated = useCallback(
    (srId: string, appointmentId: string) => {
      setCreatedAppointments((prev) => ({ ...prev, [srId]: appointmentId }));
      setExpandedSrId(null);
    },
    [],
  );

  const handleSubmit = useCallback(async () => {
    if (scheduledCount === 0) return;

    setSubmitting(true);
    setError(null);

    try {
      if (!carePlanId) {
        throw new Error("Task is not linked to a CarePlan");
      }

      const carePlan: CarePlan = await medplum.readResource(
        "CarePlan",
        carePlanId,
      );

      const appointmentSummary = schedulableRequests
        .flatMap((sr) => {
          if (!sr.id) {
            return [];
          }

          const appointmentId = createdAppointments[sr.id];
          if (!appointmentId) {
            return [];
          }

          const serviceLabel = getServiceTypeDisplay(sr);
          return [`${serviceLabel} (Appointment/${appointmentId})`];
        })
        .join("; ");

      const statusReason = `Appointments scheduled: ${appointmentSummary}`;
      const activityCode = task.code?.coding?.[0]?.code;
      if (!activityCode) {
        throw new Error("Task is missing an activity code");
      }

      const scheduledAppointmentIds = schedulableRequests.flatMap((sr) => {
        if (!sr.id) {
          return [];
        }

        const appointmentId = createdAppointments[sr.id];
        return appointmentId ? [appointmentId] : [];
      });

      const scheduledAppointments = await Promise.all(
        scheduledAppointmentIds.map(async (appointmentId) => {
          const appointment = await medplum.readResource(
            "Appointment",
            appointmentId,
          );
          if (!appointment.start) {
            throw new Error(
              `Scheduled appointment ${appointmentId} is missing a start date`,
            );
          }

          return appointment;
        }),
      );

      const latestAppointmentDate = scheduledAppointments.reduce(
        (latestDate, appointment) => {
          if (!appointment.start) {
            return latestDate;
          }
          const appointmentDate = toDateInputValue(new Date(appointment.start));
          return appointmentDate > latestDate ? appointmentDate : latestDate;
        },
        "",
      );

      await handleActivityCompletion(medplum, {
        carePlan,
        activityCode,
        statusReason,
        completionData: {
          ...(latestAppointmentDate
            ? {
                nextActivityDueDate: latestAppointmentDate,
              }
            : {}),
          ...(nextTaskOwnerOverride
            ? {
                nextActivityOwnerRef: nextTaskOwnerOverride.ref,
                nextActivityOwnerDisplay: nextTaskOwnerOverride.display,
              }
            : {}),
        },
        practitionerRole,
      });

      await teeForTask({
        task,
        minutes: careMinutes,
        method: "task-completion",
      });

      showSuccessNotification(
        `${scheduledCount} appointment${scheduledCount !== 1 ? "s" : ""} scheduled — advancing to follow-up`,
      );
      onDone();
    } catch (err) {
      setError(
        err instanceof Error
          ? err.message
          : "Failed to complete scheduling step",
      );
    } finally {
      setSubmitting(false);
    }
  }, [
    scheduledCount,
    carePlanId,
    medplum,
    schedulableRequests,
    createdAppointments,
    task,
    practitionerRole,
    nextTaskOwnerOverride,
    onDone,
    teeForTask,
    careMinutes,
  ]);

  useEffect(() => {
    onDirtyChange?.(
      scheduledCount > 0 || !!nextTaskOwnerOverride || !!careMinutes,
    );
  }, [scheduledCount, nextTaskOwnerOverride, careMinutes, onDirtyChange]);

  if (!carePlanId || !patientId) {
    return (
      <Alert icon={<IconAlertCircle size={16} />} color="red" variant="light">
        Unable to determine care plan or patient from this task.
      </Alert>
    );
  }

  return (
    <>
      <Stack gap="md">
        {error && (
          <Alert
            icon={<IconAlertCircle size={16} />}
            color="red"
            variant="light"
          >
            {error}
          </Alert>
        )}

        <SteppedForm
          form={form}
          steps={steps}
          onSubmit={() => handleSubmit()}
          isSubmitting={submitting}
          submitText={
            allScheduled
              ? "Complete & Advance to Follow-Up"
              : `Complete (${scheduledCount}/${schedulableRequests.length} scheduled)`
          }
          onCancel={onCancel}
        >
          <FormStep id="appointments">
            <Stack gap="md">
              <Text size="sm">
                Schedule appointments for each service referral. Each
                appointment will be linked to its service request and the care
                plan.
              </Text>

              {srLoading && (
                <Stack gap="xs">
                  <Skeleton height={16} />
                  <Skeleton height={80} />
                  <Skeleton height={80} />
                </Stack>
              )}

              {srError && (
                <Alert
                  icon={<IconAlertCircle size={16} />}
                  color="orange"
                  variant="light"
                >
                  Failed to load service requests: {srError}
                </Alert>
              )}

              {!srLoading && !srError && schedulableRequests.length === 0 && (
                <Alert
                  icon={<IconAlertCircle size={16} />}
                  color="orange"
                  variant="light"
                >
                  No active service requests found for this care plan.
                </Alert>
              )}

              {!srLoading &&
                !srError &&
                schedulableRequests.map((sr, index) => (
                  <ServiceAppointmentCard
                    key={sr.id}
                    sr={sr}
                    index={index}
                    totalCount={schedulableRequests.length}
                    isExpanded={expandedSrId === sr.id}
                    appointmentId={
                      sr.id ? createdAppointments[sr.id] : undefined
                    }
                    patientId={patientId}
                    carePlanId={carePlanId}
                    carePlanDisplay={carePlanDisplay}
                    onToggle={() =>
                      setExpandedSrId((prev) =>
                        prev === sr.id ? null : (sr.id ?? null),
                      )
                    }
                    onAppointmentCreated={(appointmentId) => {
                      if (sr.id) {
                        handleAppointmentCreated(sr.id, appointmentId);
                      }
                    }}
                  />
                ))}

              {scheduledCount > 0 && (
                <Text size="xs" c="dimmed">
                  {scheduledCount} of {schedulableRequests.length} appointment
                  {schedulableRequests.length !== 1 ? "s" : ""} scheduled
                </Text>
              )}
            </Stack>
          </FormStep>

          <FormStep id="assignment">
            <Stack gap="md">
              <NextTaskOwnerSection
                nextActivityLabel={nextActivityLabel}
                defaultOwner={nextActivityDefaultOwner}
                appliedRule={nextActivityAppliedRule}
                overrideOwner={nextTaskOwnerOverride}
                onOverrideChange={onNextTaskOwnerOverrideChange ?? (() => {})}
                isResolving={isResolvingNextTaskOwner}
              />
              <CareTimeActivityDurationField
                minutes={careMinutes}
                onMinutesChange={setCareMinutes}
              />
            </Stack>
          </FormStep>
        </SteppedForm>
      </Stack>
      <ConfirmationModal />
    </>
  );
});
